import React, { useState, FC } from 'react';
import { connect } from 'react-redux';
import { Collapse, Grid, Typography } from '@mui/material';
import { CloudDownload, FileCopy } from '@mui/icons-material';
import Dropzone from 'react-dropzone';
import pLimit from 'p-limit';
import {
  IStateProps,
  IDispatchProps,
  IProps,
} from './interfaces';
import {
  FormRow,
  DropContainer,
  DropNotice,
  UploadStatusBar,
  UploadStatusText,
  StyledCollapse,
  StyledExistingScheduleLabel,
  StyledExistingSchedule,
  StyledUploadMessage,
  StyledDialogHeader,
} from './styles';
import Button from '../../../Shared/VaultButton';
import {
  DialogHeaderTitle,
  DialogActionsOverride,
  ActionContainer,
} from '../../../Shared/styles/dialog';
import GameScheduleForm from '../../GameScheduleForm/component';
import { IAppState } from '../../../../state/reducers';
import {
  updateFiles,
  clearAll,
  setFileUploadStatus,
  setFileUploadProgress,
  selectIsUploadsCompleted,
  setIsUploading,
  selectIsUploading,
  IExchangeFile,
  setFileUploadAssetId,
} from '../../../../state/exchange/uploadDialog';
import { selectUploadSport } from '../../../../state/exchange/gameScheduleForm';
import { queryGamesAction } from '../../../../state/search';
import { selectExchangeQualifiersOptions, selectOptionsByName, LookupName } from '../../../../state/lookups';
import { selectIsUserVaultSuperAdmin, selectUserOrgIds } from '../../../../state/user/userOrgRole';
import { splitLast } from '../../../../utils';
import { upload, UploadToastType } from '../../../../utils/upload';
import toast, { superAdminToast } from '../../../../utils/toast';
import { EXCHANGE_COMMENT_MAX_LENGTH, Routes, VAULT_VIDEO_TYPE } from '../../../../constants';
import { VaultUploadStatus } from '../../../../state/vaultUploadDialog';
import ProgressBar from '../../../Shared/ProgressBar/component';
import { createGame, deleteSchedule, IExchangeSchedule } from '../../../../api/vault/exchangeSchedule';
import { pad2 } from '../../../../utils/generalUtils';
import { Sport } from '../../../../enums';
import { ConfirmDialog } from '../../../Shared';
import { INewExchangeSchedule } from '../../../../api/vault/assets';
import { isHockey, isLacrosse } from '../../../../utils/exchange';
import { readFileContent } from '../../../../utils/fileReader';

export interface IAssetAttachment {
  sourceFileName: string;
  attachmentName: string;
  attachmentType: string;
  content: string;
  newFile?: boolean;
}

const UploadExchange: FC<IProps> = (props: IProps) => {
  const {
    files,
    selectedSchedule,
    selectedGameType,
    homeTeam,
    awayTeam,
    selectedSeason,
    gameDate,
    homeGameNumber,
    onUpdateFiles,
    onExit,
    isUploadComplete,
    isUploading,
    userOrgIds,
    scoutedTeam,
    homeGameNumber: teamGameNumber,
    visitorGameNumber: visitorTeamGameNumber,
    selectedExchangeVideoAngle,
    sourceTeam,
    isVaultSuperAdmin,
    setFileUploadProgress,
    setFileUploadStatus,
    systemSettings,
    onSearch,
    setIsUploading,
    schedulesLoaded,
    setFileUploadAssetId,
    selectedFiles,
    uploadSport,
  } = props;

  const [barStatus, setBarStatus] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const getIfNewGameAlreadyExists = (): IExchangeSchedule | null => {
    const {
      exchangeSchedules,
      selectedSchedule,
      selectedGameType,
      gameDate,
      homeTeam,
      awayTeam,
    } = props;

    if (selectedSchedule && selectedSchedule._id === null && selectedGameType && homeTeam && awayTeam && gameDate) {
      const date = new Date(gameDate);
      const formattedGameDate = `${pad2(date.getMonth() + 1)}/${pad2(date.getDate())}/${date.getFullYear()}`;
      const foundSchedule = exchangeSchedules.find((schedule) => schedule.GameType === selectedGameType && schedule.Sport === selectedSchedule.Sport
        && schedule.League === selectedSchedule.League && schedule.HomeTeamId === homeTeam._id && schedule.VisitorTeamId === awayTeam._id
        && schedule.GameDate === formattedGameDate && schedule.Season === selectedSchedule.Season);

      return foundSchedule;
    }
    return null;
  };

  const filesHaveUniqueQualifiers = (): boolean => {
    if (isLacrosse(uploadSport)) return true;
    if (!files || files.length === 0) return false;
    const qualifiers = files.map((file) => file.qualifier).filter(Boolean);
    const uniqueQualifiers = new Set(qualifiers);
    return qualifiers.length === files.length && uniqueQualifiers.size === files.length;
  };

  const filesHaveUniqueVideAngles = (): boolean => {
    if (!isLacrosse(uploadSport)) return true;
    if (!files || files.length === 0) return false;
    const videoAngles = files.map((file) => file.videoAngle).filter(Boolean);
    const uniqueVideoAngles = new Set(videoAngles);
    return videoAngles.length === files.length && uniqueVideoAngles.size === files.length;
  };

  const onCreateAssets = async () => {
    const [userOrgId] = userOrgIds;
    const {
      NCAAGameNumber: gameId,
      GameType: gameType,
    } = selectedSchedule;
    const xchangeDate = gameDate.replace(/-/g, '/');
    const isoDate = new Date(gameDate).toISOString();
    let newSchedule: INewExchangeSchedule;

    let assetObject: any = {
      description: 'Exchange File',
      awayTeam: awayTeam.TeamName,
      awayTeamCode: awayTeam.TeamCode,
      awayTeamId: awayTeam._id,
      homeTeam: homeTeam.TeamName,
      homeTeamCode: homeTeam.TeamCode,
      homeTeamId: homeTeam._id,
      gameId,
      gameDate: isoDate,
      scoutedTeamCode: scoutedTeam ? scoutedTeam.TeamCode : null,
      teamGameNumber,
      visitorTeamGameNumber,
      videoType: VAULT_VIDEO_TYPE.Exchange,
      exchangeGameType: gameType,
      exchangeScheduleId: selectedSchedule._id,
      exchangeVideoAngle: selectedExchangeVideoAngle,
    };

    if (!selectedSchedule._id) {
      const existingSchedule = getIfNewGameAlreadyExists();
      if (existingSchedule && existingSchedule.Sport !== Sport.Softball) {
        assetObject.exchangeScheduleId = existingSchedule._id;
        assetObject.gameId = existingSchedule.NCAAGameNumber;
        assetObject.exchangeGameType = existingSchedule.GameType;
      } else {
        newSchedule = {
          HomeTeamId: homeTeam._id,
          VisitorTeamId: awayTeam._id,
          Season: selectedSchedule.Season,
          League: selectedSchedule.League,
          Sport: selectedSchedule.Sport,
          GameType: selectedGameType,
          GameDate: isoDate,
        };
      }
    }

    if (sourceTeam) {
      assetObject = {
        ...assetObject,
        sport: sourceTeam.Sport,
        exchangeSport: sourceTeam.Sport,
        league: sourceTeam.League,
        sourceTeam: sourceTeam.TeamName,
        sourceTeamCode: sourceTeam.TeamCode,
        sourceTeamId: sourceTeam._id,
      };
    }

    const getAttachments = async (file: IExchangeFile, qualifier: string): Promise<IAssetAttachment[] | null> => {
      const attachments: IAssetAttachment[] = [];
      if (file.exchangeFile) {
        const sourceFileName = file.exchangeFile.name;
        const [attachmentName, attachmentType] = splitLast(sourceFileName, '.');
        const { DOMParser, XMLSerializer } = window;

        const exchangeContent = await readFileContent(file.exchangeFile);
        if (!exchangeContent) {
          toast('Upload Failed - Invalid xchange File', {
            type: toast.TYPE.ERROR,
          });
          return null;
        }

        try {
          let content: string;
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(exchangeContent, 'text/xml');
          const gameNode = xmlDoc.querySelector('Games Game');
          if (gameNode) {
            // Create privateMetadata node if it doesn't exist.
            let privateMetadataNode = gameNode.querySelector('PrivateMetadata');
            if (!privateMetadataNode) {
              privateMetadataNode = xmlDoc.createElement('PrivateMetadata');
              gameNode.appendChild(privateMetadataNode);
            }

            let nameNode = xmlDoc.querySelector('Name');
            if (!nameNode) {
              nameNode = xmlDoc.createElement('Name');
              gameNode.insertBefore(nameNode, privateMetadataNode);
            }
            nameNode.innerHTML = `${homeTeam.TeamCode} vs. ${awayTeam.TeamCode} (${xchangeDate})`;

            let dateNode = xmlDoc.querySelector('Date');
            if (!dateNode) {
              dateNode = xmlDoc.createElement('Date');
              gameNode.insertBefore(dateNode, privateMetadataNode);
            }
            dateNode.innerHTML = xchangeDate;

            let sourceTeamNode = xmlDoc.querySelector('OriginatingTeam');
            if (!sourceTeamNode) {
              sourceTeamNode = xmlDoc.createElement('OriginatingTeam');
              gameNode.insertBefore(sourceTeamNode, privateMetadataNode);
            }
            sourceTeamNode.innerHTML = sourceTeam ? sourceTeam.TeamCode : null;

            let homeTeamNode = xmlDoc.querySelector('HomeTeam');
            if (!homeTeamNode) {
              homeTeamNode = xmlDoc.createElement('HomeTeam');
              gameNode.insertBefore(homeTeamNode, privateMetadataNode);
            }
            homeTeamNode.innerHTML = homeTeam.TeamCode;

            let awayTeamNode = xmlDoc.querySelector('VisitingTeam');
            if (!awayTeamNode) {
              awayTeamNode = xmlDoc.createElement('VisitingTeam');
              gameNode.insertBefore(awayTeamNode, privateMetadataNode);
            }
            awayTeamNode.innerHTML = awayTeam.TeamCode;

            let sportNode = xmlDoc.querySelector('Sport');
            if (!sportNode) {
              sportNode = xmlDoc.createElement('Sport');
              gameNode.insertBefore(sportNode, privateMetadataNode);
            }
            sportNode.innerHTML = sourceTeam ? sourceTeam.Sport : null;

            let locationNode = gameNode.querySelector('Location');
            if (!locationNode) {
              locationNode = xmlDoc.createElement('Location');
              gameNode.insertBefore(locationNode, privateMetadataNode);
            }
            locationNode.innerHTML = homeTeam.TeamCode;

            let exchangeTypeNode = xmlDoc.querySelector('ExchangeType');
            if (!exchangeTypeNode) {
              exchangeTypeNode = xmlDoc.createElement('ExchangeType');
              gameNode.insertBefore(exchangeTypeNode, privateMetadataNode);
            }
            exchangeTypeNode.innerHTML = qualifier;

            let openExchangeNode = privateMetadataNode.querySelector('OpenExchange');
            if (!openExchangeNode) {
              openExchangeNode = xmlDoc.createElement('OpenExchange');
              privateMetadataNode.appendChild(openExchangeNode);
            }

            let teamGameNumberNode = openExchangeNode.querySelector('TeamGameNumber');
            if (!teamGameNumberNode) {
              teamGameNumberNode = xmlDoc.createElement('TeamGameNumber');
              openExchangeNode.appendChild(teamGameNumberNode);
            }
            teamGameNumberNode.innerHTML = teamGameNumber;

            let gameNumberNode = openExchangeNode.querySelector('NCAAGameNumber');
            if (!gameNumberNode) {
              gameNumberNode = xmlDoc.createElement('NCAAGameNumber');
              openExchangeNode.appendChild(gameNumberNode);
            }
            gameNumberNode.innerHTML = gameId;

            let phaseNode = openExchangeNode.querySelector('Phase');
            if (!phaseNode) {
              phaseNode = xmlDoc.createElement('Phase');
              openExchangeNode.appendChild(phaseNode);
            }
            phaseNode.innerHTML = qualifier;

            content = new XMLSerializer().serializeToString(xmlDoc.documentElement);
          }

          attachments.push({
            sourceFileName,
            attachmentName,
            attachmentType,
            content,
          });
        } catch (err) {
          toast('Upload Failed - Invalid xchange File', { type: toast.TYPE.ERROR });
          console.error('[QA] Error encountered while parsing and sanitizing xchange document', err.toString());
          return null;
        }
      }

      // Add other attachments
      if (file.other && file.other.length > 0) {
        let failedMessage: string;
        const otherAttachments: IAssetAttachment[] = await Promise.all(
          file.other.map(async (otherFile) => {
            if (failedMessage) return null;
            const [attachmentName, attachmentType] = splitLast(otherFile.name, '.');
            const content = await readFileContent(otherFile);
            if (!content) {
              failedMessage = `Upload Failed - Error reading file: ${otherFile.name}`;
              return null;
            }
            return {
              sourceFileName: otherFile.name,
              attachmentName,
              attachmentType,
              content,
            };
          }),
        );
        if (failedMessage) {
          toast(failedMessage, { type: toast.TYPE.ERROR });
          return null;
        }
        return [...attachments, ...otherAttachments];
      }

      return attachments;
    };

    interface UploadToastEntry {
      toasts: Array<UploadToastType>;
      finishedToast: UploadToastType;
      fileName: string;
    }

    // Create schedule if needed
    let newlyCreatedSchedule: IExchangeSchedule;
    if (newSchedule) {
      try {
        const response = await createGame({ ...newSchedule });
        if (response?.data?.data) {
          newlyCreatedSchedule = response?.data?.data;
          assetObject.exchangeScheduleId = newlyCreatedSchedule._id;
          assetObject.gameId = newlyCreatedSchedule.NCAAGameNumber;
        }
      } catch (e) {
        console.error('[QA] Error creating new schedule', e);
        toast('Upload Failed - Unable to create new schedule', {
          type: toast.TYPE.ERROR,
        });
        files.forEach((file, index) => {
          setFileUploadStatus(index, VaultUploadStatus.Error);
        });
        return;
      }
    }

    console.log('Uploading files:', files);

    // Indexed by file position, not by execution order. The per-file setters below write
    // into the Redux selectedFiles array, which is in iteration order, while pLimit(3) runs
    // the callbacks in whatever order slots free up - so a counter incremented inside the
    // callback drifts from the file it belongs to and status/progress land on the wrong row.
    const uploadToasts: Array<UploadToastEntry> = files.map((file) => ({
      toasts: [],
      finishedToast: null,
      fileName: file.filename,
    }));
    const totalUploads = files.length;
    const limit = pLimit(3);
    const pendingUploads: Promise<any>[] = [];
    files.forEach((file, curIndex) => {
      pendingUploads.push(limit(async () => {
        const attachments = await getAttachments(file, file.qualifier);
        if (!attachments) {
          uploadToasts[curIndex].finishedToast = UploadToastType.uploadError;
          setFileUploadStatus(curIndex, VaultUploadStatus.Error);
          return;
        }
        setFileUploadStatus(curIndex, VaultUploadStatus.Initializing);
        if (!window.EXCHANGE_UPLOAD_CANCELED) {
          await upload(
            file.mediaFile,
            userOrgId,
            VAULT_VIDEO_TYPE.Exchange,
            systemSettings,
            {
              ...assetObject,
              exchangeQualifier: file.qualifier,
              exchangeVideoAngle: file.videoAngle,
              // Trimmed, so what is sent is what filesHaveValidComments measured - otherwise
              // whitespace could carry a payload past the check that enabled Create.
              exchangeComment: file.comment?.trim(),
              attachments,
            },
            {
              status: (uploadStatus: VaultUploadStatus) => {
                setFileUploadStatus(curIndex, uploadStatus);
              },
              progress: (progressPercentage: string) => {
                setFileUploadProgress(curIndex, progressPercentage);
              },
              toast: (toastType: UploadToastType) => {
                uploadToasts[curIndex].toasts.push(toastType);
                if (toastType === UploadToastType.created) {
                  if (!uploadToasts[curIndex].finishedToast) uploadToasts[curIndex].finishedToast = toastType;
                } else if (toastType === UploadToastType.uploadError) {
                  uploadToasts[curIndex].finishedToast = toastType;
                  if (totalUploads === 1) toast(`Error uploading: ${uploadToasts[curIndex].fileName}`, { type: toast.TYPE.ERROR });
                }
              },
              assetId: (assetId: string) => {
                setFileUploadAssetId(curIndex, assetId);
              },
            },
            isVaultSuperAdmin,
          );
        }
      }));
    });
    await Promise.all(pendingUploads);

    const failedUploads = uploadToasts.filter((entry) => entry.finishedToast === UploadToastType.uploadError).map((entry) => entry.fileName);
    const completedUploads = uploadToasts.filter((entry) => entry.finishedToast === UploadToastType.created);
    if (totalUploads > 1) {
      // Single toast for all completed
      if (completedUploads.length > 0) {
        superAdminToast(isVaultSuperAdmin, `Successfully Uploaded ${completedUploads.length} Files`, { type: toast.TYPE.SUCCESS });
      }

      // Single toast for all failed
      let errors = '';
      if (failedUploads.length > 0) {
        if (errors.length > 0) errors += '\n';
        errors += `Failed Uploads: ${failedUploads.join(', ')}`;
      }
      if (errors.length > 0) superAdminToast(isVaultSuperAdmin, errors, { type: toast.TYPE.ERROR });
    }

    // Cleanup newly created schedule if all uploads failed
    if (failedUploads.length === totalUploads && newlyCreatedSchedule) {
      await deleteSchedule(newlyCreatedSchedule._id, true);
    }

    const { location: { pathname: route } } = window;
    if (route === Routes.HOME) onSearch(1000);
  };

  const getUploadStatusText = (uploadStatus: VaultUploadStatus, progressPercentage?: string): string => {
    switch (uploadStatus) {
      case VaultUploadStatus.PreProcessing:
        return `Step 1 of 2: Scanning - ${progressPercentage || '0'}%`;
      case VaultUploadStatus.Uploading:
        return `Step 2 of 2: Uploading - ${progressPercentage || '0'}%`;
      case VaultUploadStatus.CheckTranscode:
        return 'Checking for Transcode';
      case VaultUploadStatus.Initializing:
        return 'Initializing';
      case VaultUploadStatus.Complete:
        return 'Completed';
      case VaultUploadStatus.Error:
        return 'Error occurred';
      case VaultUploadStatus.NotStarted:
        return 'Not Started';
      default:
        return '';
    }
  };

  const startUpload = async () => {
    setBarStatus(true);
    setIsUploading(true);
    await onCreateAssets();
    setIsUploading(false);
  };

  const uploadStarted = files && files.length > 0 && barStatus;

  const cancelButtonDisabled = !(uploadStarted || !isUploading) || isUploadComplete;
  // VV-226: vault-api rejects an over-length comment with a 400, so blocking here keeps the
  // upload from failing partway through the fan-out with several assets already created.
  const filesHaveValidComments = (): boolean => !(files || [])
    .some((file) => (file.comment || '').trim().length > EXCHANGE_COMMENT_MAX_LENGTH);

  const saveButtonDisabled = (!selectedSchedule || (!selectedGameType && selectedSchedule._id === null) || !gameDate
    || !homeGameNumber || !homeTeam || !awayTeam || !selectedSeason || !filesHaveUniqueQualifiers() || !filesHaveUniqueVideAngles()
    || !filesHaveValidComments())
    || (isUploading && !isUploadComplete);

  // Mirrors saveButtonDisabled so a disabled Create button can say what it's waiting on.
  // Creating a game on the fly leaves several of these blank, and with no game-number or
  // qualifier row visible for some sports it isn't obvious which one is missing.
  const missingBeforeUpload = (): string[] => {
    const missing: string[] = [];
    if (!selectedSeason) missing.push('Season');
    if (!selectedSchedule) {
      missing.push('Game');
    } else if (!selectedGameType && selectedSchedule._id === null) {
      missing.push('Game Type');
    }
    if (!gameDate) missing.push('Game Date');
    if (!homeGameNumber) missing.push('Game #');
    if (!homeTeam) missing.push('Home Team');
    if (!awayTeam) missing.push('Visitor Team');
    if (!filesHaveUniqueQualifiers()) missing.push('a unique qualifier on every file');
    if (!filesHaveUniqueVideAngles()) missing.push('a unique video angle on every file');
    if (!filesHaveValidComments()) missing.push(`every comment to be ${EXCHANGE_COMMENT_MAX_LENGTH} characters or fewer`);
    return missing;
  };
  const missing = saveButtonDisabled && !isUploading ? missingBeforeUpload() : [];

  // Check if new game and schedule already exists
  const newScheduleFullySelected = ((selectedSchedule && selectedSchedule._id === null) || selectedGameType || gameDate
    || homeGameNumber || homeTeam || awayTeam || selectedSeason);
  let existingSchedule;
  if (newScheduleFullySelected) {
    existingSchedule = getIfNewGameAlreadyExists();
  }

  const existingScheduleString = existingSchedule ? `${existingSchedule.GameDate} ${homeTeam.TeamName} vs ${awayTeam.TeamName} (${existingSchedule.NCAAGameNumber})` : null;
  const checkForDuplicates = (((!selectedSchedule) || selectedSchedule.Sport !== Sport.Softball) && (existingScheduleString));
  const showDropzone = !(barStatus);
  return (
    <>
      <StyledDialogHeader
        data-auto="ExchangeUploadDialog"
      >
        <DialogHeaderTitle className="title">
          Upload Exchange Asset
        </DialogHeaderTitle>
        {schedulesLoaded && (
          <StyledCollapse in={showDropzone} className="dropzone">
            <Dropzone onDrop={onUpdateFiles}>
              {({ getRootProps, getInputProps }) => (
                <DropContainer
                  {...getRootProps()}
                  data-auto="exchangeFileDrop"
                >
                  <input {...getInputProps()} />
                  <DropNotice>
                    <CloudDownload /> Drop Files Here (media file, xchange{isHockey(uploadSport) ? ', xml' : ''})
                  </DropNotice>
                </DropContainer>
              )}
            </Dropzone>
          </StyledCollapse>
        )}
      </StyledDialogHeader>
      {uploadStarted && !isUploadComplete && (<StyledUploadMessage>You can ONLY leave this page once the upload says Complete</StyledUploadMessage>)}
      {files && files.length > 0 && barStatus && files.map((file, index) => (
        <Grid container sx={{ alignItems: 'center', padding: '12px' }} key={index}>
          <Grid xs={5} item>
            <span title={file.filename}>{file.filename}</span>
          </Grid>
          <Grid xs={4} item>
            <UploadStatusBar>
              <ProgressBar stage={file.uploadStatus} percent={file.progressPercentage} />
            </UploadStatusBar>
          </Grid>
          <Grid xs={3} item>
            <UploadStatusText>
              {`${getUploadStatusText(file.uploadStatus, file.progressPercentage)}`}
            </UploadStatusText>
          </Grid>
        </Grid>
      ))}
      <StyledCollapse in={!barStatus}>
        <GameScheduleForm isSeasonSelectable={!!files} />
      </StyledCollapse>
      {(checkForDuplicates && (!isUploading && !isUploadComplete)) && (
        <StyledExistingScheduleLabel>Uploading to Existing Game: <StyledExistingSchedule>{existingScheduleString}</StyledExistingSchedule></StyledExistingScheduleLabel>
      )}
      {(missing.length > 0 && !isUploadComplete && files && files.length > 0) && (
        <StyledExistingScheduleLabel data-auto="UploadMissingFields">
          Still needed: <StyledExistingSchedule>{missing.join(', ')}</StyledExistingSchedule>
        </StyledExistingScheduleLabel>
      )}
      <DialogActionsOverride disableSpacing>
        <ActionContainer>
          <Button
            variant="plainOnPaper"
            onClick={isUploading ? () => { setOpenConfirmDialog(true); } : onExit}
            data-auto="UploadCancelBtn"
            disabled={cancelButtonDisabled}
          >
            {'Cancel'}
          </Button>
          <Button
            variant="primaryOnWhite"
            onClick={isUploadComplete ? onExit : startUpload}
            disabled={saveButtonDisabled}
            data-auto="UploadCreateBtn"
          >
            {isUploadComplete || isUploading ? 'Done' : 'Create'}
          </Button>
        </ActionContainer>
      </DialogActionsOverride>
      <ConfirmDialog
        open={openConfirmDialog}
        onResult={(r) => {
          if (r.confirmed) onExit();
          setOpenConfirmDialog(false);
        }}
        title={'Cancel Upload'}
        message={'Are you sure you want to cancel the upload?'}
      />
    </>
  );
};

const mapStateToProps = (state: IAppState): IStateProps => ({
  isOpen: state.exchange.uploadDialog.isOpen,
  files: state.exchange.uploadDialog.selectedFiles,
  selectedSeason: state.exchange.gameScheduleForm.selectedSeason,
  selectedSchedule: state.exchange.gameScheduleForm.selectedSchedule,
  selectedExchangeVideoAngle: state.exchange.gameScheduleForm.selectedExchangeVideoAngle,
  selectedGameType: state.exchange.gameScheduleForm.selectedExchangeGameType,
  gameDate: state.exchange.gameScheduleForm.gameDate,
  homeGameNumber: state.exchange.gameScheduleForm.homeGameNumber,
  visitorGameNumber: state.exchange.gameScheduleForm.visitorGameNumber,
  homeTeam: state.exchange.gameScheduleForm.homeTeam,
  awayTeam: state.exchange.gameScheduleForm.awayTeam,
  sourceTeam: state.exchange.gameScheduleForm.sourceTeam,
  scoutedTeam: state.exchange.gameScheduleForm.scoutedTeam,
  exchangeQualifierOptions: selectExchangeQualifiersOptions(state),
  iptcCategoryOptions: selectOptionsByName(LookupName.iptcCategory, state),
  userOrgIds: selectUserOrgIds(state),
  systemSettings: state.systemSettings.systemSettings,
  isVaultSuperAdmin: selectIsUserVaultSuperAdmin(state),
  isUploadComplete: selectIsUploadsCompleted(state),
  isUploading: selectIsUploading(state),
  exchangeSchedules: state.exchange.gameScheduleForm.exchangeSchedules,
  schedulesLoaded: state.exchange.gameScheduleForm.isLoaded,
  selectedFiles: state.exchange.uploadDialog.selectedFiles,
  uploadSport: selectUploadSport(state),
});

const mapDispatchToProps: IDispatchProps = {
  onUpdateFiles: updateFiles,
  onSearch: queryGamesAction,
  onExit: clearAll,
  setFileUploadStatus,
  setFileUploadProgress,
  setIsUploading,
  setFileUploadAssetId,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(UploadExchange);
